'use client';

import type {KeyboardEvent, ReactNode} from 'react';
import {useRef, useState} from 'react';
import {clsx} from 'clsx';

import {OverviewMap} from './overview-map';
import type {RegionId} from './regions-data';
import {REGION_ORDER, REGIONS} from './regions-data';

type Props = {
	defaultContent: ReactNode;
	nordreLandContent: ReactNode;
	sondreLandContent: ReactNode;
	vestreTotenContent: ReactNode;
	ostreTotenContent: ReactNode;
	gjovikContent: ReactNode;
};

export const RegionsMapClient = ({
	defaultContent,
	nordreLandContent,
	sondreLandContent,
	gjovikContent,
	ostreTotenContent,
	vestreTotenContent,
}: Props) => {
	const [selectedRegion, setSelectedRegion] = useState<RegionId | null>(null);
	const [hoveredRegion, setHoveredRegion] = useState<RegionId | null>(null);
	const [focusedRegion, setFocusedRegion] = useState<RegionId | null>(null);
	const tabRefs = useRef<Partial<Record<RegionId, HTMLButtonElement | null>>>(
		{},
	);
	const isMouseInteraction = useRef(false);

	const contentMap: Record<RegionId, ReactNode> = {
		nordreLand: nordreLandContent,
		gjovik: gjovikContent,
		sondreLand: sondreLandContent,
		vestreToten: vestreTotenContent,
		ostreToten: ostreTotenContent,
	};
	const content = selectedRegion
		? contentMap[selectedRegion]
		: defaultContent;

	const getTabIndex = (id: RegionId) =>
		selectedRegion === id ||
		(selectedRegion === null && id === REGION_ORDER[0])
			? 0
			: -1;

	const handleKeyDown = (
		e: KeyboardEvent<HTMLButtonElement>,
		id: RegionId,
	) => {
		const currentIndex = REGION_ORDER.indexOf(id);
		let nextIndex: number | null = null;

		switch (e.key) {
			case 'ArrowRight':
			case 'ArrowDown':
				e.preventDefault();
				nextIndex = (currentIndex + 1) % REGION_ORDER.length;
				break;
			case 'ArrowLeft':
			case 'ArrowUp':
				e.preventDefault();
				nextIndex =
					(currentIndex - 1 + REGION_ORDER.length) %
					REGION_ORDER.length;
				break;
			case 'Home':
				e.preventDefault();
				nextIndex = 0;
				break;
			case 'End':
				e.preventDefault();
				nextIndex = REGION_ORDER.length - 1;
				break;
		}
		if (nextIndex !== null) {
			e.stopPropagation();
			const next = REGION_ORDER[nextIndex];
			setSelectedRegion(next);
			tabRefs.current[next]?.focus();
		}
	};

	return (
		<div className="relative flex flex-col items-center gap-8 lg:flex-row">
			{/* Map + accessible tablist */}
			<div className="relative aspect-square w-full max-w-[500px] lg:flex-[40%]">
				{/* Overview of norway */}
				<OverviewMap
					aria-hidden
					className={clsx(
						'w-[150px]',
						'sm:w-[200px]',
						'absolute',
						'right-0',
						'top-0',
						'-z-10',
					)}
				/>
				{/* Accessible tablist – visually hidden, screen readers only */}
				<div
					role="tablist"
					aria-label="Velg en region"
					className="sr-only"
				>
					{REGIONS.map((region) => (
						<button
							key={region.id}
							type="button"
							role="tab"
							id={`tab-${region.id}`}
							aria-selected={selectedRegion === region.id}
							aria-controls="region-panel"
							tabIndex={getTabIndex(region.id)}
							ref={(el) => {
								tabRefs.current[region.id] = el;
							}}
							onClick={() => setSelectedRegion(region.id)}
							onFocus={(e) => {
								if (
									!isMouseInteraction.current &&
									e.currentTarget.matches(':focus-visible')
								) {
									setFocusedRegion(region.id);
								}
								isMouseInteraction.current = false;
							}}
							onBlur={() => setFocusedRegion(null)}
							onKeyDown={(e) => handleKeyDown(e, region.id)}
							className="sr-only"
						>
							{region.label}
						</button>
					))}
				</div>

				{/* Visual SVG map – decorative, mouse interaction only */}
				<svg
					viewBox="0 0 365.61 355.71"
					xmlns="http://www.w3.org/2000/svg"
					aria-hidden="true"
					focusable="false"
					className="size-full"
				>
					{/* Static decorative polygon */}
					<polygon
						points="135.05 77.11 136.34 75.85 136.32 75.82 135.05 77.11"
						fill="#04433b"
					/>

					{REGIONS.map((region) => {
						const isActive =
							selectedRegion === region.id ||
							hoveredRegion === region.id;
						const isFocused = focusedRegion === region.id;
						const fill = isActive ? '#c5e3d7' : '#04433b';
						const textFill = isActive ? '#04433b' : '#fff';

						return (
							<g
								key={region.id}
								onClick={() => {
									isMouseInteraction.current = true;
									setSelectedRegion(region.id);
									tabRefs.current[region.id]?.focus();
								}}
								onMouseEnter={() => setHoveredRegion(region.id)}
								onMouseLeave={() => setHoveredRegion(null)}
								className="cursor-pointer"
							>
								{/* Shape – swap between default and hover geometry when active */}
								{isActive ? (
									region.hoverElement === 'path' ? (
										<path
											d={region.hoverD}
											fill={fill}
											fillRule="evenodd"
										/>
									) : (
										<polygon
											points={region.hoverD}
											fill={fill}
											fillRule="evenodd"
										/>
									)
								) : region.defaultElement === 'path' ? (
									<path d={region.defaultD} fill={fill} />
								) : (
									<polygon
										points={region.defaultD}
										fill={fill}
									/>
								)}

								{/* Focus ring – dashed stroke following the active shape boundary */}
								{isFocused &&
									(region.hoverElement === 'path' ? (
										<path
											d={region.hoverD}
											fill="none"
											stroke="#04433b"
											strokeWidth="2"
											strokeDasharray="5 2"
										/>
									) : (
										<polygon
											points={region.hoverD}
											fill="none"
											stroke="#04433b"
											strokeWidth="2"
											strokeDasharray="5 2"
										/>
									))}

								{/* Label text */}
								<text
									transform={region.labelTransform}
									fill={textFill}
									fontFamily="Satoshi-Medium, Satoshi"
									fontSize="12.76"
									fontWeight="500"
								>
									{region.labelTspans}
								</text>
							</g>
						);
					})}
				</svg>
			</div>

			{/* Tabpanel */}
			<div
				id="region-panel"
				role="tabpanel"
				aria-labelledby={
					selectedRegion ? `tab-${selectedRegion}` : undefined
				}
				tabIndex={0}
				className="w-full outline-none lg:flex-[60%]"
			>
				{content}
			</div>
		</div>
	);
};
